import {
  KeyPersonRoleType,
  ROLE_ORDER,
  useEditRoleAssignmentDates,
} from '@commenda/api';
import type { IKeyPersonRoleAssignment } from '@commenda/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { RoleConfigSheet } from './AssignRoleStep/components';
import type { ConfigDraft } from './AssignRoleStep/types';
import { EMPTY_DRAFT } from './AssignRoleStep/utils';

// A UBO role is ended through the ownership-aware flow, so only its start date is editable here.
const UBO_ATTRIBUTE_KEY = 'UBO';

const isRoleType = (key: string): key is KeyPersonRoleType =>
  (ROLE_ORDER as readonly string[]).includes(key);

const draftFromRole = (role: IKeyPersonRoleAssignment): ConfigDraft => ({
  ...EMPTY_DRAFT(),
  startDate: role.startDate ?? undefined,
  endDate: role.endDate ?? undefined,
});

/** The assign-role sheet, opened on an existing assignment with the entity fixed; saves dates only. */
export const EditRoleSheet = ({
  keyPersonId,
  companyId,
  role,
  onClose,
  onSaved,
}: {
  keyPersonId: number;
  companyId: number;
  // The assignment being edited; null keeps the sheet closed.
  role: IKeyPersonRoleAssignment | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [draft, setDraft] = useState<ConfigDraft>(EMPTY_DRAFT);
  // Kept after close so the sheet body renders through the slide-out.
  const [shownRole, setShownRole] = useState<IKeyPersonRoleAssignment | null>(
    null
  );
  const { setNotification } = useNotification();
  const mutation = useEditRoleAssignmentDates(keyPersonId);

  useEffect(() => {
    if (!role) return;
    setShownRole(role);
    setDraft(draftFromRole(role));
  }, [role]);

  const roleType =
    shownRole && isRoleType(shownRole.attributeKey)
      ? shownRole.attributeKey
      : null;
  const isUbo = shownRole?.attributeKey === UBO_ATTRIBUTE_KEY;
  const entityName = shownRole
    ? (shownRole.corporationName ?? `Entity #${shownRole.corporationId}`)
    : '';

  const handleSave = async () => {
    if (!role) return;
    try {
      await mutation.mutateAsync({
        assignmentId: role.assignmentId,
        startDate: draft.startDate || null,
        ...(isUbo ? {} : { endDate: draft.endDate || null }),
      });
      setNotification({
        show: true,
        valid: true,
        title: 'Role updated',
        message: `${role.label}${
          role.detail ? ` (${role.detail})` : ''
        } at ${entityName} saved.`,
      });
      onSaved();
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to update role',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <RoleConfigSheet
      open={role !== null}
      roleType={roleType}
      entityName={entityName}
      selectedCount={1}
      isEditing
      companyId={companyId}
      draft={draft}
      onPatch={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
      onClose={onClose}
      onAdd={handleSave}
      sharedFields={[]}
      onAddSharedField={() => {}}
      onRemoveSharedField={() => {}}
      allowedGovernanceRoles={[]}
      applyToAllDisabledReason={null}
      datesOnly
      lockedDetail={shownRole?.detail ?? null}
      lockEndDate={isUbo}
    />
  );
};
